/**
 * DisputePanel.jsx — Inline dispute management panel shown inside DealDetail.
 *
 * Features:
 * - Shows all disputes for the deal with status, reason, retention window
 * - Chat thread per dispute (max 10 messages)
 * - Party who raised dispute can mark it resolved
 * - Milestone-level dispute button per milestone
 * - 2-day retention window countdown after deal COMPLETED or milestone CONFIRMED
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/auth';

const DISPUTE_STATUS_COLORS = {
  OPEN: 'warning',
  RESOLVED_BUYER: 'success',
  RESOLVED_BIDDER: 'success',
  ESCALATED: 'error',
  DISMISSED: 'default',
};

const ROLE_COLORS = { BUYER: '#1976d2', BIDDER: '#388e3c', ADMIN: '#7b1fa2' };

function RetentionBadge({ expiresAt, windowOpen }) {
  if (!windowOpen) return <Chip label="Dispute window closed" size="small" color="default" />;
  if (!expiresAt) return <Chip label="Dispute window open" size="small" color="success" />;

  const msLeft = new Date(expiresAt) - new Date();
  const hoursLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60)));
  return (
    <Chip
      label={`Dispute window: ${hoursLeft}h remaining`}
      size="small"
      color={hoursLeft < 6 ? 'error' : 'warning'}
    />
  );
}

function ChatThread({ disputeId, disputeStatus, viewerAnon, viewerRole }) {
  const [messages, setMessages] = useState([]);
  const [remaining, setRemaining] = useState(10);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/api/v1/disputes/${disputeId}/chat/`);
      setMessages(data.messages || []);
      setRemaining(data.messages_remaining ?? 0);
    } catch {
      setError('Could not load messages.');
    } finally {
      setLoading(false);
    }
  }, [disputeId]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    setError('');
    try {
      const { data } = await apiClient.post(`/api/v1/disputes/${disputeId}/chat/`, { message: text.trim() });
      setMessages((prev) => [...prev, data]);
      setRemaining(data.messages_remaining ?? 0);
      setText('');
    } catch (err) {
      const code = err.response?.data?.error?.code;
      if (code === 'CHAT_LIMIT_REACHED') {
        setError('Message limit reached (10/10). Please resolve or escalate the dispute.');
      } else {
        setError(err.response?.data?.error?.message || 'Failed to send message.');
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) return <CircularProgress size={20} sx={{ mt: 1 }} />;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Chat ({messages.length}/10 messages)
        </Typography>
        {remaining === 0 && (
          <Chip label="Chat limit reached" size="small" color="error" variant="outlined" />
        )}
      </Box>

      {/* Message thread */}
      <Box sx={{ maxHeight: 280, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, mb: 1, bgcolor: 'background.default' }}>
        {messages.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
            No messages yet. Start the conversation to resolve this dispute.
          </Typography>
        )}
        {messages.map((m) => {
          const isMe = m.is_mine;
          return (
            <Box
              key={m.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isMe ? 'flex-end' : 'flex-start',
                mb: 1,
              }}
            >
              <Box
                sx={{
                  maxWidth: '80%',
                  bgcolor: isMe ? 'primary.main' : 'background.paper',
                  color: isMe ? 'white' : 'text.primary',
                  border: isMe ? 'none' : '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  px: 1.5, py: 0.75,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, color: isMe ? 'rgba(255,255,255,0.8)' : ROLE_COLORS[m.sender_role] || 'text.secondary', display: 'block', mb: 0.25 }}>
                  {m.sender_role}{m.sender_role === 'ADMIN' ? ' (Admin)' : ''}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.message}</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, px: 0.5 }}>
                {new Date(m.sent_at).toLocaleString()}
              </Typography>
            </Box>
          );
        })}
        <div ref={bottomRef} />
      </Box>

      {/* Send box */}
      {disputeStatus === 'OPEN' && remaining > 0 && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={`Send a message (${remaining} remaining)…`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            inputProps={{ maxLength: 2000 }}
            multiline
            maxRows={3}
          />
          <IconButton color="primary" onClick={handleSend} disabled={sending || !text.trim()} aria-label="Send message">
            {sending ? <CircularProgress size={20} /> : <SendIcon />}
          </IconButton>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

export default function DisputePanel({ dealId, disputes, viewerAnon, viewerRole, onDisputeResolved, retentionInfo }) {
  const [expandedId, setExpandedId] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [resolveError, setResolveError] = useState('');
  const navigate = useNavigate();

  const handleSelfResolve = async (disputeId) => {
    setResolving(disputeId);
    setResolveError('');
    try {
      await apiClient.post(`/api/v1/disputes/${disputeId}/resolve/`);
      onDisputeResolved?.();
    } catch (err) {
      setResolveError(err.response?.data?.error?.message || 'Failed to resolve dispute.');
    } finally {
      setResolving(null);
    }
  };

  if (!disputes || disputes.length === 0) return null;

  return (
    <Box>
      {resolveError && <Alert severity="error" sx={{ mb: 1 }}>{resolveError}</Alert>}
      {disputes.map((disp) => {
        const raisedByMe = String(disp.raised_by_anonymous_id) === String(viewerAnon);
        const isOpen = disp.dispute_status === 'OPEN';
        const expanded = expandedId === disp.id;

        return (
          <Paper
            key={disp.id}
            variant="outlined"
            sx={{ mb: 1.5, borderColor: isOpen ? 'warning.main' : 'divider' }}
          >
            <Box
              sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', cursor: 'pointer' }}
              onClick={() => setExpandedId(expanded ? null : disp.id)}
            >
              <Typography variant="subtitle2">Dispute #{disp.id}</Typography>
              <Chip label={disp.dispute_status} size="small" color={DISPUTE_STATUS_COLORS[disp.dispute_status] || 'default'} />
              {disp.milestone_id && <Chip label="Milestone dispute" size="small" variant="outlined" />}
              <Chip label={raisedByMe ? 'Raised by you' : 'Raised by other party'} size="small" variant="outlined" />
              {disp.legal_escalation_recommended && (
                <Chip label="Legal review suggested" size="small" color="error" variant="outlined" />
              )}
              <Box sx={{ ml: 'auto' }}>
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Box>
            </Box>

            <Collapse in={expanded}>
              <Divider />
              <Box sx={{ p: 1.5 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                  <strong>Reason:</strong> {disp.dispute_reason}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Opened: {new Date(disp.created_at).toLocaleString()}
                  {disp.resolved_at ? ` · Resolved: ${new Date(disp.resolved_at).toLocaleString()}` : ''}
                </Typography>

                {/* Self-resolve button — only for the party who raised it */}
                {isOpen && raisedByMe && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={resolving === disp.id ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                    disabled={resolving === disp.id}
                    onClick={() => handleSelfResolve(disp.id)}
                    sx={{ mb: 1.5 }}
                    aria-label="Mark dispute as resolved"
                  >
                    Mark as Resolved
                  </Button>
                )}

                {/* Chat thread */}
                <ChatThread
                  disputeId={disp.id}
                  disputeStatus={disp.dispute_status}
                  viewerAnon={viewerAnon}
                  viewerRole={viewerRole}
                />

                {disp.has_active_arbitration && disp.arbitration_id && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<MeetingRoomIcon />}
                      onClick={() => navigate(`/arbitrations/${disp.arbitration_id}`)}
                    >
                      Enter Arbitration Room
                    </Button>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Paper>
        );
      })}
    </Box>
  );
}
